import type { FandomTaxonomyValue } from "../types";

import {
  MAX_NUMBER_OF_VALUES,
  MAX_VALUE_LENGTH,
  type TaxonomyKeysConfiguration,
} from "../consts";
import { normalizeConfidenceScore } from "../FandomTaxonomyHelpers";
import { getValuesFromTaxonomyWithPrefix } from "./getValuesFromTaxonomyWithPrefix";

export function getTaxonomyValueForKey(
  config: TaxonomyKeysConfiguration,
): string[] {
  const { from, addConfidence = false, limit } = config;

  // fetch the data from the taxonomy object and prefix values with 's_' or 'p_'
  // add confidence to the values if needed
  const siteValues = withConfidence(
    getValuesFromTaxonomyWithPrefix("site", from),
    addConfidence === "site" || addConfidence === "all",
  );
  const pageValues = withConfidence(
    getValuesFromTaxonomyWithPrefix("page", from),
    addConfidence === "page" || addConfidence === "all",
  );

  // limit the values if needed in this special case
  const slice = limit === "one-of-each" ? 1 : undefined;
  const combinedValues = orderByConfidence([
    ...siteValues.slice(0, slice),
    ...pageValues.slice(0, slice),
  ]);

  // return string values
  const values = combinedValues.filter(Boolean).map(getNormalizedValue);

  // limit the number of values
  return values.slice(0, MAX_NUMBER_OF_VALUES);
}

// Sort the values by confidence
function orderByConfidence(
  values: FandomTaxonomyValue[],
): FandomTaxonomyValue[] {
  return values.sort((a, b) => b.confidence - a.confidence);
}

function withConfidence(
  values: FandomTaxonomyValue[],
  addConfidence: boolean,
): FandomTaxonomyValue[] {
  if (addConfidence) {
    return values.map(addConfidenceToValue);
  }
  return values;
}

// Add confidence category to the value
function addConfidenceToValue(value: FandomTaxonomyValue): FandomTaxonomyValue {
  return {
    ...value,
    value: `${value.value}${normalizeConfidenceScore(value.confidence)}`,
  };
}

// This function is used to get the value from the taxonomy value, and normalize it
function getNormalizedValue(value: FandomTaxonomyValue): string {
  return value.value.substring(0, MAX_VALUE_LENGTH);
}
